import React, { useState, useEffect, useRef } from 'react';

/**
 * AddressAutocompleteInput
 * 
 * Provides a "smart fill-in" address search bar.
 * As the user types, it queries the backend address autocomplete service and shows a dropdown
 * of likely addresses. When selected, it provides structured components:
 * street, street2, city, state, zip, country, country_code, latitude, longitude.
 */
export default function AddressAutocompleteInput({
  onSelectAddress,
  placeholder = "Start typing address for smart fill-in (e.g. 120 Albert St Auckland)...",
  countryFilter = "",
  disabled = false,
  apiBase,
  style = {}
}) {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const resolvedApiBase = apiBase !== undefined ? apiBase : (isDev ? `http://${window.location.hostname}:8000` : '');

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch address suggestions from backend proxy
  const fetchSuggestions = async (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({ q: query.trim(), limit: '6' });
      if (countryFilter) {
        params.append('country', countryFilter.trim());
      }

      const res = await fetch(`${resolvedApiBase}/api/v1/address/autocomplete?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        const list = data.suggestions || [];
        setSuggestions(list);
        setIsOpen(list.length > 0);
        setHighlightIndex(-1);
      } else {
        setSuggestions([]);
        setIsOpen(false);
      }
    } catch (err) {
      console.warn('Address autocomplete error:', err);
      setSuggestions([]);
      setIsOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (val.trim().length >= 2) {
      debounceTimerRef.current = setTimeout(() => {
        fetchSuggestions(val);
      }, 250);
    } else {
      setSuggestions([]);
      setIsOpen(false);
    }
  };

  const handleSelect = (suggestion) => {
    setInputValue(suggestion.formatted_address || suggestion.street || '');
    setIsOpen(false);
    setSuggestions([]);
    if (onSelectAddress) {
      onSelectAddress(suggestion);
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter') {
      if (highlightIndex >= 0 && highlightIndex < suggestions.length) {
        e.preventDefault();
        handleSelect(suggestions[highlightIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', ...style }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {/* Search / Lightning Icon */}
        <span
          style={{
            position: 'absolute',
            left: '10px',
            color: '#38bdf8',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            pointerEvents: 'none'
          }}
          title="Smart Address Fill-In"
        >
          ⚡
        </span>

        <input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setIsOpen(true);
          }}
          disabled={disabled}
          style={{
            width: '100%',
            backgroundColor: '#0f172a',
            border: '1px solid rgba(56, 189, 248, 0.4)',
            boxShadow: '0 0 0 1px rgba(56, 189, 248, 0.15)',
            color: '#ffffff',
            borderRadius: '6px',
            padding: '8px 32px 8px 30px',
            fontSize: '12px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />

        {/* Loading Spinner or Clear Button */}
        {loading ? (
          <span style={{
            position: 'absolute',
            right: '10px',
            width: '14px',
            height: '14px',
            border: '2px solid rgba(255,255,255,0.2)',
            borderTopColor: '#38bdf8',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite',
            display: 'inline-block'
          }} />
        ) : inputValue ? (
          <button
            type="button"
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '8px',
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              fontSize: '14px',
              lineHeight: 1,
              padding: '2px 4px'
            }}
            title="Clear search"
          >
            ×
          </button>
        ) : null}
      </div>

      {/* Floating Suggestions Dropdown */}
      {isOpen && suggestions.length > 0 && (
        <ul
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 9999,
            backgroundColor: '#1e293b',
            border: '1px solid rgba(56, 189, 248, 0.3)',
            borderRadius: '6px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)',
            margin: 0,
            padding: '4px 0',
            listStyle: 'none',
            maxHeight: '240px',
            overflowY: 'auto'
          }}
        >
          <li style={{
            padding: '4px 12px',
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: '#94a3b8',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>Suggested Addresses</span>
            <span style={{ color: '#38bdf8' }}>Courier Verified Format</span>
          </li>

          {suggestions.map((s, idx) => {
            const isHighlighted = idx === highlightIndex;
            return (
              <li
                key={idx}
                onMouseEnter={() => setHighlightIndex(idx)}
                onClick={() => handleSelect(s)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  backgroundColor: isHighlighted ? 'rgba(56, 189, 248, 0.15)' : 'transparent',
                  borderBottom: idx < suggestions.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                  transition: 'background-color 0.12s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ fontWeight: 600, color: '#f8fafc', fontSize: '12px' }}>
                    {s.street || s.formatted_address.split(',')[0]}
                  </div>
                  {s.country_code && (
                    <span style={{
                      backgroundColor: 'rgba(56, 189, 248, 0.2)',
                      color: '#38bdf8',
                      fontSize: '10px',
                      fontWeight: 700,
                      padding: '1px 5px',
                      borderRadius: '4px',
                      letterSpacing: '0.05em'
                    }}>
                      {s.country_code.toUpperCase()}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px', lineHeight: 1.3 }}>
                  {[s.city, s.state, s.zip, s.country].filter(Boolean).join(', ')}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
